import { isNumber, isObject, isString } from "@common/helpers/typeGuards";
import { v4 as uuid } from "uuid";
import { isUUID } from "@common/helpers/uuid";
import {
    isRuleConditionSimilarTo,
    parseSimilarToValue,
} from "@common/helpers/split-aware-description.helper";
import { Category } from "@common/types/category";
import {
    AmountRoundingLevel,
    RuleConditionAmount,
    RuleConditionDate,
    RuleConditionType,
    TransactionRuleAction,
    TransactionRuleCondition,
    TransactionRuleConditionDefinition,
    TransferRuleDirection,
} from "@common/types/transactionRule";
import {
    AmountConditionType,
    AmountRoundingConditionType,
    DateConditionType,
    DescriptionConditionType,
    FormRuleConditionType,
    TransactionRuleActionFormSchema,
    TransactionRuleConditionFormSchema,
    mapRuleActionTypeToForm,
    mapRuleActionTypeToFormValue,
} from "./constants";

interface ConditionModelToFormRowParams {
    conditionModel: TransactionRuleConditionDefinition & { id?: string };
    categoriesById: Record<string, Category>;
}

export function conditionModelToFormRow({
    conditionModel: input,
    categoriesById,
}: ConditionModelToFormRowParams):
    | TransactionRuleConditionFormSchema
    | undefined {
    const conditionModel: TransactionRuleCondition = {
        ...input,
        id: input.id ?? uuid(),
    };
    switch (conditionModel.conditionType) {
        case RuleConditionType.CATEGORY:
            return buildCategoryConditionFormRow(
                conditionModel,
                categoriesById,
            );
        case RuleConditionType.CLASS:
            return buildClassConditionFormRow(conditionModel);
        case RuleConditionType.SUGGESTED_CATEGORY:
            return buildSuggestedCategoryConditionFormRow(
                conditionModel,
                categoriesById,
            );
        case RuleConditionType.AMOUNT:
            return buildAmountConditionFormRow(conditionModel);
        case RuleConditionType.DATE:
            return buildDateConditionFormRow(conditionModel);
        case RuleConditionType.DESCRIPTION_INCLUDES:
        case RuleConditionType.DESCRIPTION_IS:
            return buildDescriptionConditionFormRow(conditionModel);
        case RuleConditionType.FINANCIAL_ACCOUNTS:
            return buildAccountConditionFormRow(conditionModel);
        case RuleConditionType.SUBACCOUNTS:
            return buildSubaccountConditionFormRow(conditionModel);
        case RuleConditionType.ENTITY:
            return buildEntityConditionFormRow(conditionModel);
        case RuleConditionType.COUNTERPARTY:
            return buildCounterpartyConditionFormRow(conditionModel);
        case RuleConditionType.DATE_PROXIMITY:
            return buildDateProximityConditionFormRow(conditionModel);
        case RuleConditionType.SIMILAR_TO:
            return buildSimilarToConditionFormRow(conditionModel);
        case RuleConditionType.DESCRIPTION_DOES_NOT_INCLUDE:
            return buildDescriptionConditionFormRow(conditionModel);
        case RuleConditionType.AMOUNT_IS_ROUND:
            return buildRoundingAsAmountFormRow(conditionModel, false);
        case RuleConditionType.AMOUNT_IS_NOT_ROUND:
            return buildRoundingAsAmountFormRow(conditionModel, true);
    }
}

function buildClassConditionFormRow(conditionModel: TransactionRuleCondition) {
    if (conditionModel.value) {
        return {
            ...getDefaultFormRow(),
            id: conditionModel.id,
            conditionType: FormRuleConditionType.CLASS,
            classIds: conditionModel.value as string[],
        };
    }
}

function buildCategoryConditionFormRow(
    conditionModel: TransactionRuleCondition,
    categoriesById: Record<string, Category>,
) {
    if (
        isString(conditionModel.value) &&
        categoriesById[conditionModel.value]
    ) {
        return {
            ...getDefaultFormRow(),
            id: conditionModel.id,
            conditionType: FormRuleConditionType.CATEGORY,
            categoryIds: [conditionModel.value],
            ...buildTransferDirection(conditionModel),
        };
    }

    if (Array.isArray(conditionModel.value)) {
        return {
            ...getDefaultFormRow(),
            id: conditionModel.id,
            conditionType: FormRuleConditionType.CATEGORY,
            categoryIds: conditionModel.value as string[],
            ...buildTransferDirection(conditionModel),
        };
    }
}

function buildSuggestedCategoryConditionFormRow(
    conditionModel: TransactionRuleCondition,
    categoriesById: Record<string, Category>,
) {
    if (
        isString(conditionModel.value) &&
        categoriesById[conditionModel.value]
    ) {
        return {
            ...getDefaultFormRow(),
            id: conditionModel.id,
            conditionType: FormRuleConditionType.SUGGESTED_CATEGORY,
            categoryIds: [conditionModel.value],
            ...buildTransferDirection(conditionModel),
        };
    }
}

function buildDateConditionFormRow(conditionModel: TransactionRuleCondition) {
    if (isDateCondition(conditionModel.value)) {
        return {
            ...getDefaultFormRow(),
            id: conditionModel.id,
            conditionType: FormRuleConditionType.DATE,
            dateConditionType: getDateConditionType(conditionModel.value),
            dateValueRange: conditionModel.value,
            ...buildTransferDirection(conditionModel),
        };
    }
}

function buildAmountConditionFormRow(conditionModel: TransactionRuleCondition) {
    if (isAmountCondition(conditionModel.value)) {
        return {
            ...getDefaultFormRow(),
            id: conditionModel.id,
            conditionType: FormRuleConditionType.AMOUNT,
            amountConditionType: getAmountConditionType(conditionModel.value),
            amountIsCredit: amountConditionIsCredit(conditionModel.value),
            amountSingleValue: getSingleAmountValue(conditionModel.value),
            amountValueRange: getAmountValueRange(conditionModel.value),
            ...buildTransferDirection(conditionModel),
        };
    }
}

const mapRuleconditionTypeToDescriptionConditionType = (
    conditionType: RuleConditionType,
) => {
    switch (conditionType) {
        case RuleConditionType.DESCRIPTION_IS:
            return DescriptionConditionType.IS;
        case RuleConditionType.DESCRIPTION_INCLUDES:
            return DescriptionConditionType.CONTAINS;
        case RuleConditionType.DESCRIPTION_DOES_NOT_INCLUDE:
            return DescriptionConditionType.DOES_NOT_CONTAIN;
        default:
            return DescriptionConditionType.IS;
    }
};

function buildDescriptionConditionFormRow(
    conditionModel: TransactionRuleCondition,
) {
    return {
        ...getDefaultFormRow(),
        id: conditionModel.id,
        conditionType: FormRuleConditionType.BANK_DESCRIPTION,
        descriptionConditionType:
            mapRuleconditionTypeToDescriptionConditionType(
                conditionModel.conditionType,
            ),
        descriptionValue: isString(conditionModel.value)
            ? [conditionModel.value]
            : (conditionModel.value as string[]),
        ...buildTransferDirection(conditionModel),
    };
}

function buildAccountConditionFormRow(
    conditionModel: TransactionRuleCondition,
) {
    let accountValue: number[] = [];
    if (isNumber(conditionModel.value)) {
        accountValue = [conditionModel.value];
    } else if (Array.isArray(conditionModel.value)) {
        accountValue = conditionModel.value as number[];
    }

    return {
        ...getDefaultFormRow(),
        id: conditionModel.id,
        conditionType: FormRuleConditionType.ACCOUNT,
        accountValue,
        ...buildTransferDirection(conditionModel),
    };
}

function buildSubaccountConditionFormRow(
    conditionModel: TransactionRuleCondition,
) {
    let subaccountValue: string[] = [];
    if (isString(conditionModel.value)) {
        subaccountValue = [conditionModel.value];
    } else if (Array.isArray(conditionModel.value)) {
        subaccountValue = conditionModel.value as string[];
    }

    return {
        ...getDefaultFormRow(),
        id: conditionModel.id,
        conditionType: FormRuleConditionType.ACCOUNT,
        subaccountValue,
        ...buildTransferDirection(conditionModel),
    };
}

function buildEntityConditionFormRow(conditionModel: TransactionRuleCondition) {
    if (isNumber(conditionModel.value) || Array.isArray(conditionModel.value)) {
        return {
            ...getDefaultFormRow(),
            id: conditionModel.id,
            conditionType: FormRuleConditionType.ENTITY,
            entityValue: isNumber(conditionModel.value)
                ? [conditionModel.value]
                : conditionModel.value.filter(isNumber),
            ...buildTransferDirection(conditionModel),
        };
    }
}

function buildCounterpartyConditionFormRow(
    conditionModel: TransactionRuleCondition,
) {
    if (isString(conditionModel.value)) {
        return {
            ...getDefaultFormRow(),
            id: conditionModel.id,
            conditionType: FormRuleConditionType.COUNTERPARTY,
            counterpartyIds: [conditionModel.value],
            ...buildTransferDirection(conditionModel),
        };
    }
    if (Array.isArray(conditionModel.value)) {
        return {
            ...getDefaultFormRow(),
            id: conditionModel.id,
            conditionType: FormRuleConditionType.COUNTERPARTY,
            counterpartyIds: (conditionModel.value as string[]).filter(
                (value) => isUUID(value),
            ),
            ...buildTransferDirection(conditionModel),
        };
    }
}

function buildDateProximityConditionFormRow(
    conditionModel: TransactionRuleCondition,
) {
    return {
        ...getDefaultFormRow(),
        id: conditionModel.id,
        conditionType: FormRuleConditionType.DATE_PROXIMITY,
        dateProximity: conditionModel.value as number,
    };
}

function isAmountCondition(
    value: TransactionRuleCondition["value"],
): value is RuleConditionAmount {
    return isObject(value) && ("min" in value || "max" in value);
}

function isDateCondition(
    value: TransactionRuleCondition["value"],
): value is RuleConditionDate {
    return isObject(value) && ("after" in value || "before" in value);
}

function getDateConditionType(value: RuleConditionDate): DateConditionType {
    if (value.after && value.before) {
        return DateConditionType.BETWEEN;
    }
    if (value.after) {
        return DateConditionType.AFTER;
    }
    return DateConditionType.BEFORE;
}

function amountConditionIsCredit(value: RuleConditionAmount): boolean {
    return (
        (isNumber(value.min) && value.min >= 0) ||
        (isNumber(value.max) && value.max > 0)
    );
}

function getSingleAmountValue(amount: RuleConditionAmount): number | undefined {
    if (
        isNumber(amount.min) &&
        isNumber(amount.max) &&
        amount.min === amount.max
    ) {
        return Math.abs(amount.min);
    }
    if (isNumber(amount.min) && !isNumber(amount.max)) {
        return Math.abs(amount.min);
    }
    if (isNumber(amount.max) && !isNumber(amount.min)) {
        return Math.abs(amount.max);
    }
}

function getAmountValueRange(
    amount: RuleConditionAmount,
): RuleConditionAmount | undefined {
    if (isNumber(amount.min) && isNumber(amount.max)) {
        if (amount.min >= 0) {
            return {
                min: amount.min,
                max: amount.max,
            };
        } else {
            return {
                min: Math.abs(amount.max),
                max: Math.abs(amount.min),
            };
        }
    }
}

function getAmountConditionType(
    value: RuleConditionAmount,
): AmountConditionType {
    if (isNumber(value.min) && isNumber(value.max)) {
        if (value.min === value.max) {
            return AmountConditionType.IS;
        }
        return AmountConditionType.BETWEEN;
    }
    if (
        (isNumber(value.min) && value.min >= 0) ||
        (isNumber(value.max) && value.max <= 0)
    ) {
        return AmountConditionType.GREATER_THAN;
    }
    return AmountConditionType.LESS_THAN;
}

type Optional<T, K extends keyof T> = Omit<T, K> & Partial<T>;

export function getDefaultFormRow(): Omit<
    TransactionRuleConditionFormSchema,
    "id"
> {
    return {
        amountIsCredit: true,
        amountConditionType: AmountConditionType.GREATER_THAN,
        amountSingleValue: 0,
        descriptionConditionType: DescriptionConditionType.CONTAINS,
        dateConditionType: DateConditionType.AFTER,
        transferDirection: null,
    };
}

export function modelToFormActions(
    actions?: Array<Optional<TransactionRuleAction, "id">>,
): TransactionRuleActionFormSchema[] {
    const result: TransactionRuleActionFormSchema[] = [];

    // Add actions from the new actions array if available
    if (actions?.length) {
        actions.forEach((action) => {
            try {
                const actionToAdd: TransactionRuleActionFormSchema = {
                    id: action.id ?? uuid(),
                    actionType: mapRuleActionTypeToForm(action.actionType),
                    value: mapRuleActionTypeToFormValue(
                        action.actionType,
                        action.value,
                    ),
                    ...buildTransferDirection(action),
                };
                result.push(actionToAdd);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn("Skipping invalid action:", error);
            }
        });
    }

    return result;
}

function buildSimilarToConditionFormRow(
    conditionModel: TransactionRuleCondition,
) {
    if (
        isString(conditionModel.value) ||
        isRuleConditionSimilarTo(conditionModel.value)
    ) {
        const { normalizedDescriptionId, effectiveSplitKey } =
            parseSimilarToValue(conditionModel.value);
        return {
            ...getDefaultFormRow(),
            id: conditionModel.id,
            conditionType: FormRuleConditionType.SIMILAR_TO,
            normalizedDescriptionId,
            effectiveSplitKey,
        };
    }
}

const AMOUNT_ROUNDING_MODEL_TO_FORM: Record<
    string,
    AmountRoundingConditionType
> = {
    [AmountRoundingLevel.DOLLAR]: AmountRoundingConditionType.DOLLAR,
    [AmountRoundingLevel.TEN]: AmountRoundingConditionType.TEN,
    [AmountRoundingLevel.HUNDRED]: AmountRoundingConditionType.HUNDRED,
    [AmountRoundingLevel.THOUSAND]: AmountRoundingConditionType.THOUSAND,
};

function buildRoundingAsAmountFormRow(
    conditionModel: TransactionRuleCondition,
    isNegated: boolean,
) {
    if (isObject(conditionModel.value) && "level" in conditionModel.value) {
        const { level, isCredit } = conditionModel.value as {
            level: string;
            isCredit: boolean;
        };
        return {
            ...getDefaultFormRow(),
            id: conditionModel.id,
            conditionType: FormRuleConditionType.AMOUNT,
            amountConditionType: isNegated
                ? AmountConditionType.IS_NOT_ROUND
                : AmountConditionType.IS_ROUND,
            amountIsCredit: isCredit,
            amountRoundingLevel: isNegated
                ? undefined
                : (AMOUNT_ROUNDING_MODEL_TO_FORM[level] ??
                  AmountRoundingConditionType.DOLLAR),
            ...buildTransferDirection(conditionModel),
        };
    }
}

function buildTransferDirection(
    model: Omit<TransactionRuleCondition | TransactionRuleAction, "id">,
): {
    transferDirection: TransferRuleDirection | null;
} {
    return model.transferDirection
        ? { transferDirection: model.transferDirection }
        : { transferDirection: null };
}
